import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabaseClient';
import { logger } from '../utils/logger';
import { normalizeAvatarUrl } from './useUserProfiles';

export interface UserProfile {
  id: string;
  full_name: string | null;
  email: string;
  role: 'admin' | 'user' | 'agent' | null;
  avatar_url: string | null;
  phone_number: string | null;
  created_at: string;
  updated_at: string | null;
}

/**
 * Hook to fetch and cache user profile using React Query
 * This prevents multiple unnecessary calls to Supabase
 */
export const useUserProfile = (userId: string | undefined) => {
  return useQuery({
    queryKey: ['userProfile', userId],
    queryFn: async (): Promise<UserProfile | null> => {
      if (!userId) return null;

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', userId)
          .single();

        if (error) {
          logger.error('❌ Error fetching user profile:', error);
          throw error;
        }

        if (data) {
          const profile: UserProfile = {
            ...data,
            avatar_url: normalizeAvatarUrl(data.avatar_url),
          };

          logger.log('✅ User profile fetched (cached):', {
            userId,
            role: profile?.role,
            email: profile?.email,
            full_name: profile?.full_name,
            avatar_url: profile?.avatar_url ? 'present' : 'missing',
          });

          return profile;
        }

        return null;
      } catch (err) {
        logger.error('❌ Exception fetching user profile:', err);
        throw err;
      }
    },
    enabled: !!userId,
    staleTime: 30 * 60 * 1000, // 30 minutes - avatares no cambian frecuentemente
    gcTime: 60 * 60 * 1000, // 1 hour - mantener en cache más tiempo
    retry: 2, // Retry up to 2 times on error
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnMount: false, // Don't refetch on mount if data is fresh
    refetchOnReconnect: true, // Refetch on reconnect
  });
};

/**
 * Hook to invalidate user profile cache
 * Use this after updating the profile to refresh the cache
 */
export const useInvalidateUserProfile = () => {
  const queryClient = useQueryClient();

  return (userId: string) => {
    queryClient.invalidateQueries({ queryKey: ['userProfile', userId] });
    logger.log('🔄 User profile cache invalidated for:', userId);
  };
};

